import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3'
import { S3RequestPresigner } from '@aws-sdk/s3-request-presigner'
import { createRequest } from '@aws-sdk/util-create-request'
import { formatUrl } from '@aws-sdk/util-format-url'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { message, Modal, Tooltip } from 'antd'
import Amplify, { Auth, API } from 'aws-amplify'
import clsx from 'clsx'
import { find, get, isEqual, reject, upperFirst } from 'lodash'
import React from 'react'
import * as assetActions from '../actions/asset'
import * as builderActions from '../actions/builders'
import * as endpointActions from '../actions/endpoint'
import AssetSelector from '../../components/common/AssetSelector'
import ReviewAssetForm from '../../components/library/ReviewAssetForm'
import * as analyticsConstants from '../constants/analytics'
import { DEFAULT_SIDEBAR_WIDTH } from '../constants/app'
import {
  PDF_IMAGE_FILE_TYPE,
  TRANSCODING_EXTENSION,
  UGC_REJECTED_STATUS,
} from '../constants/assets'
import {
  BUILDERS_WITH_SLIDES,
  COMPARISON__BUILDER_TYPE,
  FEATURE__BUILDER_TYPE,
  NAVIGATOR__BUILDER_TYPE,
  NEWS__BUILDER_TYPE,
  PRODUCT__BUILDER_TYPE,
  SOLUTION__BUILDER_TYPE,
} from '../constants/builders'
import * as endpointConstants from '../constants/endpoints'
import { WEB_ENDPOINT_TYPE } from '../constants/endpoints'
import * as folderConstants from '../constants/folder'
import { EXTENSIONS } from './extensions'
import { builderConfigs } from '../../components/builders/builderConfigs'

/**
 * Retrieves current jwtToken from wherever Amplify keeps is stored.
 * We rely on this because it's the easiest way to ensure the refresh
 * token gets refreshed automatically by AWS amplify.
 *
 * (copied from `mobile-sales-tool/src/dux/amplify.js`)
 */
const getAuthHeader = async () => {
  const session = await Auth.currentSession()
  const jwt = session.getIdToken().getJwtToken()
  return { Authorization: `Bearer ${jwt}` }
}

/* configures amplify */
/* if userId is passed, analytics endpoint is configured for the user */
export const configureAmplify = (config) => {
  // Amplify.Logger.LOG_LEVEL = 'DEBUG';
  Amplify.configure({
    Auth: {
      region: config.region,
      userPoolWebClientId: config.appClient,
      userPoolId: config.userPool,
      identityPoolId: config.identityPool,
      cookieStorage: {
        domain: window.location.hostname, //config.mask || config.domain,
        path: '/',
        expires: 1,
        sameSite: 'lax',
        secure: false,
      },
    },
    API: {
      endpoints: [
        {
          name: 'BAM',
          endpoint: `${config.baseUrl}/api`,
          custom_header: getAuthHeader,
        },
      ],
      aws_appsync_graphqlEndpoint: config.appSyncEndpoint,
      aws_appsync_region: config.region,
      aws_appsync_authenticationType: 'AWS_IAM',
    },
    aws_appsync_graphqlEndpoint: config.appSyncEndpoint,
    aws_appsync_region: config.region,
    aws_appsync_authenticationType: 'AWS_IAM',
  })
}

export const recordAnalyticsEvent = async (actionType, data = {}) => {
  const name = getEventName(actionType)
  const attributes = getEventAttributes(actionType, data)
  try {
    const event = {
      event_type: name,
      event_timestamp: Date.now(),
      event_version: '10.0',
      attributes,
    }
    await API.post('BAM', '/analytics/events', {
      body: { events: [event] },
    })
  } catch (err) {
    console.error('error posting analytics event', err)
  }
}

export const getEventName = (actionType) => {
  switch (actionType) {
    case assetActions.GET_ASSET:
      return analyticsConstants.VIEW_ASSET_DETAIL

    case assetActions.CREATE_ASSET:
    case builderActions.CREATE_BUILDER:
      return analyticsConstants.CREATE_ASSET

    case analyticsConstants.DOWNLOAD_ASSET:
      return analyticsConstants.DOWNLOAD_ASSET

    case analyticsConstants.SHARE_ASSET:
      return analyticsConstants.SHARE_ASSET

    case assetActions.UPDATE_ASSET:
    case builderActions.UPDATE_BUILDER:
      return analyticsConstants.EDIT_ASSET

    case endpointActions.PUBLISH_ASSET:
      return analyticsConstants.PUBLISH_ASSET_REQUEST

    case analyticsConstants.PUBLISH_ASSET_APPROVE:
      return analyticsConstants.PUBLISH_ASSET_APPROVE

    case analyticsConstants.PUBLISH_ASSET_REJECT:
      return analyticsConstants.PUBLISH_ASSET_REJECT

    case endpointActions.UNPUBLISH_ASSET:
      return analyticsConstants.UNPUBLISH_ASSET_REQUEST

    case analyticsConstants.UNPUBLISH_ASSET_APPROVE:
      return analyticsConstants.UNPUBLISH_ASSET_APPROVE

    case analyticsConstants.UNPUBLISH_ASSET_REJECT:
      return analyticsConstants.UNPUBLISH_ASSET_REJECT
    case analyticsConstants.CALCULATE:
      return analyticsConstants.CALCULATE
    case analyticsConstants.LAUNCH_CALCULATOR:
      return analyticsConstants.LAUNCH_CALCULATOR
    case analyticsConstants.FORM_SUBMIT:
      return analyticsConstants.FORM_SUBMIT
    case analyticsConstants.QUIZ_SUBMIT:
      return analyticsConstants.QUIZ_SUBMIT
    default:
      return analyticsConstants.GENERIC_EVENT
  }
}

export const getEventAttributes = (actionType, data) => {
  switch (actionType) {
    case assetActions.GET_ASSET:
    case builderActions.UPDATE_BUILDER:
    case assetActions.UPDATE_ASSET:
    case analyticsConstants.EDIT_ASSET:
    case analyticsConstants.DOWNLOAD_ASSET:
      return {
        assetId: data.asset.id,
        assetVersion: data.asset.version.toString(),
        file_type: data.asset.file_type,
      }

    case analyticsConstants.SHARE_ASSET:
      return {
        assetId: data.asset.id,
        assetVersion: data.asset.version.toString(),
        file_type: data.asset.file_type,
        share_url_id: data.url.ID,
      }

    case assetActions.CREATE_ASSET:
    case builderActions.CREATE_BUILDER:
    case endpointActions.PUBLISH_ASSET:
    case analyticsConstants.PUBLISH_ASSET_APPROVE:
    case analyticsConstants.PUBLISH_ASSET_REJECT:
    case endpointActions.UNPUBLISH_ASSET:
    case analyticsConstants.UNPUBLISH_ASSET_APPROVE:
    case analyticsConstants.UNPUBLISH_ASSET_REJECT:
      return {
        assetId: data.asset.id,
        assetVersion: data.asset.version.toString(),
        file_type: data.asset.file_type,
        assetSource: data.asset.tags.indexOf('SYS.ugc') > -1 ? 'UGC' : 'DAM',
      }
    case analyticsConstants.CALCULATE:
      return calculateAttributes(data)
    case analyticsConstants.LAUNCH_CALCULATOR:
      return {
        calculatorId: data.calculatorId,
        title: data.title,
      }
    case analyticsConstants.FORM_SUBMIT:
      return formSubmitAttributes(data)
    case analyticsConstants.QUIZ_SUBMIT:
      return quizSubmitAttributes(data)
    default:
      return {
        actionType,
        generic: 'true',
      }
  }
}

/*
 * flattenCalculatorObject/CalculateAttributes - utilities for getting the data in the right shape
 * for the CALCULATE analytics event
 */

function flattenCalculatorObject(calcObject, firstKey) {
  const flattenedObject = {}
  const sectionKeys = Object.keys(calcObject[firstKey])
  let keyCount = 0
  for (let i = 0; i < sectionKeys.length; ++i) {
    const section = calcObject[firstKey][sectionKeys[i]]
    const ioKeys = Object.keys(section)
    for (let j = 0; j < ioKeys.length; ++j) {
      const io = section[ioKeys[j]]
      flattenedObject[
        `${firstKey}|${String(keyCount).padStart(2, '0')}|${ioKeys[j]}`
      ] = io
      ++keyCount
    }
  }

  return flattenedObject
}

function calculateAttributes(action) {
  const attributes = {
    calculatorId: action.calculatorId,
    title: action.title,
  }

  const flattenedInputs = flattenCalculatorObject(action, 'in')
  Object.keys(flattenedInputs).forEach((inputKey) => {
    attributes[inputKey.substring(0, 50)] = flattenedInputs[inputKey]
  })

  const flattenedOutputs = flattenCalculatorObject(action, 'out')
  Object.keys(flattenedOutputs).forEach((outputKey) => {
    attributes[outputKey.substring(0, 50)] = flattenedOutputs[outputKey]
  })
  return attributes
}

/*
 * formSubmitAttributes - utility for getting form submission data in the right shape
 * for the FORM_SUBMIT analytics event. Similar to calculateAttributes but for form submissions.
 *
 * action.analyticsConfig can contain:
 *   - fields: { include: [...] } or { exclude: [...] } to filter which fields are captured
 *   - tableColumns: [...] columns to show in analytics detail view
 *   - exportColumns: [...] columns to include in CSV export
 */
function formSubmitAttributes(action) {
  var attributes = {
    formId: action.formId,
    title: action.title,
  }

  // Include results if provided (e.g., matched products from product finder)
  // Store as out|00|results to follow calculator output convention
  if (action.results) {
    attributes['out|00|results'] = String(action.results)
  }

  var analyticsConfig = action.analyticsConfig || {}

  // Determine which fields to include based on config
  var fieldsConfig = analyticsConfig.fields || {}
  var includeFields = fieldsConfig.include || null
  var excludeFields = fieldsConfig.exclude || []

  // Flatten form values into attributes with 'in|' prefix (similar to calculator inputs)
  var values = action.values || {}
  var keys = Object.keys(values)
  var fieldIndex = 0
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i]
    var value = values[key]

    // Skip internal/system fields (prefixed with underscore or hyphen)
    if (key.charAt(0) === '_' || key.charAt(0) === '-') {
      continue
    }

    // Apply include filter (if specified, only include these fields)
    if (includeFields && !includeFields.includes(key)) {
      continue
    }

    // Apply exclude filter
    if (excludeFields.includes(key)) {
      continue
    }

    // Format key similar to calculator: in|00|fieldName
    var formattedKey = 'in|' + String(fieldIndex).padStart(2, '0') + '|' + key
    fieldIndex++
    // Truncate key to 50 chars and convert value to string
    attributes[formattedKey.substring(0, 50)] =
      value !== null && value !== undefined ? String(value) : ''
  }

  return attributes
}

/*
 * quizSubmitAttributes - utility for getting quiz result data in the right shape
 * for the QUIZ_SUBMIT analytics event.
 */
function quizSubmitAttributes(data) {
  var attributes = {
    assetId: data.assetId || '',
    title: data.title || 'Quiz',
    score: String(data.score !== undefined ? data.score : 0) + '%',
    passed: String(!!data.passed),
    correctCount: String(
      data.correctCount !== undefined ? data.correctCount : 0,
    ),
    totalCount: String(data.totalCount !== undefined ? data.totalCount : 0),
  }

  var answers = data.answers || []
  for (var i = 0; i < answers.length; i++) {
    var a = answers[i]
    var key = 'q|' + String(i).padStart(2, '0') + '|' + (a.question || '')
    attributes[key.substring(0, 50)] = a.chosen || ''
    var correctKey =
      'qr|' + String(i).padStart(2, '0') + '|' + (a.question || '')
    attributes[correctKey.substring(0, 50)] = String(!!a.correct)
  }

  return attributes
}

/*
 * buildPath - recursively walks the parentID chain to
 *  returns an array of folder names
 */
let _store = null
export function setStore(s) {
  _store = s
}

export function displayFolderName(name) {
  if (name === 'MST Root') {
    return (
      (_store &&
        _store.getState().settings &&
        _store.getState().settings.menu &&
        _store.getState().settings.menu.modules &&
        _store.getState().settings.menu.modules.library) ||
      'Library'
    )
  }
  // Stored as "User Generated Content". Form uploads still land here.
  if (name === 'User Generated Content') return 'Uploads'
  return name
}

// The published endpoint's stored name is "Mobile Sales Tool" on most tenants.
// Show it as "Published", the label the Library sidebar uses.
export function displayEndpointName(endpoint) {
  return endpoint.id === folderConstants.MST_ENDPOINT
    ? 'Published'
    : endpoint.name
}

export function buildPath(folder, allFolders) {
  let path = []
  if (allFolders[folder]) {
    path.push(allFolders[folder].id)
    let current = folder
    while (allFolders[current] && allFolders[current].parentId) {
      current = allFolders[current].parentId

      // BAM-2502 may not exist due to being filtered out from privileges
      if (allFolders[current]) {
        path.push(allFolders[current].id)
      }
    }
  }

  return path
}

/*
 * Test if f1 is a child folder of f2
 * f1 = target folder
 * f2 = droppedItem folder
 */
/*
 * orderedChildFolders - the order a folder's children are DISPLAYED in.
 *
 * A folder that has been reordered in the tile grid carries an `itemOrder` of
 * folder AND asset ids, and when it exists it wins over `childFolders` in both
 * the tile grid (TileView) and the sidebar tree (TreeNode). Anything that
 * reasons about sibling position - the tree itself, and the drag/drop hover
 * bookkeeping - has to go through here, or it computes positions against an
 * order the user isn't looking at.
 *
 * Children absent from `itemOrder` are appended rather than dropped, and ids
 * are de-duplicated: `itemOrder` is maintained by $push on the server and has
 * no uniqueness guarantee, and a repeated id would mean duplicate React keys.
 */
export function orderedChildFolders(node) {
  const childFolders = (node && node.childFolders) || []
  const itemOrder = node && node.itemOrder
  if (!itemOrder || itemOrder.length === 0) return childFolders

  const seen = {}
  const ordered = itemOrder.filter((id) => {
    if (seen[id] || childFolders.indexOf(id) === -1) return false
    seen[id] = true
    return true
  })
  return ordered.concat(childFolders.filter((id) => !seen[id]))
}

export function isChild(f1, f2, allFolders) {
  if (allFolders[f2].childFolders.indexOf(f1) > -1) {
    return true
  } else {
    let result = false
    allFolders[f2].childFolders &&
      allFolders[f2].childFolders.forEach((folder) => {
        if (isChild(f1, folder, allFolders)) {
          result = true
        }
      })
    return result
  }
  //console.log(allFolders[f1].name,'not a child of ', allFolders[f2].name)
  //return false
}

export const getChildren = (folder, allFolders) => {
  let children = allFolders[folder].childFolders
  allFolders[folder].childFolders.forEach((child) => {
    children = children.concat(getChildren(child, allFolders))
  })
  return children
}

export const assetName = (asset, maxLength = 30) => {
  let name, fullname
  if (asset && asset.title && asset.title.length > 0) {
    name =
      asset.title.length > maxLength
        ? asset.title
            .substring(0, maxLength - 9)
            .concat('...')
            .concat(asset.title.substring(asset.title.length - 6))
        : asset.title
    fullname = asset.title
  } else {
    name =
      asset.filename && asset.filename.length > maxLength
        ? asset.filename
            .substring(0, maxLength - 9)
            .concat('...')
            .concat(asset.filename.substring(asset.filename.length - 6))
        : asset.filename
    fullname = asset.filename
  }

  if (name === '') name = 'Unitled'
  return <Tooltip title={fullname}>{name}</Tooltip>
}

export const shortenFolderName = (folderName = '') => {
  if (!folderName) return ''
  if (folderName.length < 24) return folderName

  const beginning = folderName.substring(0, 10).trim()
  const middle = '...'
  const end = folderName.substring(folderName.length - 10).trim()

  return beginning.concat(middle).concat(end)
}

/*
 *   Return the preview icon for the asset; for images it will be the URL, for other file types
 *   there are default icons in the S3 asset bucket
 */
export function getFileDetails(mime_type, url, extension) {
  let displayDetails = {
    preview: null,
    icon: 'fa-file',
    color: '#003a8c',
  }
  let foundMimeType = false
  const paths = [
    { mime: 'image', path: url, icon: 'fa-file-image-o', color: null },
    { mime: 'audio', path: null, icon: 'fa-file-audio-o', color: '#780650' },
    { mime: 'video', path: null, icon: 'fa-file-video-o', color: '#00474f' },
    {
      mime: 'application/pdf',
      path: null,
      icon: 'fa-file-pdf-o',
      color: '#820014',
    },
    {
      mime: 'application/msword',
      path: null,
      icon: 'fa-file-word-o',
      color: '#061178',
    },
    {
      mime: 'application/vnd.ms-word',
      path: null,
      icon: 'fa-file-word-o',
      color: '#061178',
    },
    {
      mime: 'application/vnd.oasis.opendocument.text',
      path: null,
      icon: 'fa-file-word-o',
      color: '#061178',
    },
    {
      mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml',
      path: null,
      icon: 'fa-file-word-o',
      color: '#061178',
    },
    {
      mime: 'application/vnd.ms-excel',
      path: null,
      icon: 'fa-file-excel-o',
      color: '#135200',
    },
    {
      mime: 'application/vnd.ms-excel',
      path: null,
      icon: 'fa-file-excel-o',
      color: '#135200',
    },
    {
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml',
      path: null,
      icon: 'fa-file-excel-o',
      color: '#135200',
    },
    {
      mime: 'application/vnd.openxmlformats-officedocument.presentationml',
      path: null,
      icon: 'fa-file-powerpoint-o',
      color: '#873800',
    },
    {
      mime: 'application/vnd.ms-powerpoint',
      path: null,
      icon: 'fa-file-powerpoint-o',
      color: '#873800',
    },
    {
      mime: 'application/vnd.openxmlformats-officedocument.presentationml',
      path: null,
      icon: 'fa-file-powerpoint-o',
      color: '#873800',
    },
    {
      mime: 'application/vnd.oasis.opendocument.presentation',
      path: null,
      icon: 'fa-file-powerpoint-o',
      color: '#873800',
    },
  ]

  // Try to get details by mime type
  paths.forEach((mt) => {
    if (!foundMimeType && mime_type.startsWith(mt.mime)) {
      displayDetails.preview = mt.path
      displayDetails.icon = mt.icon
      displayDetails.color = mt.color
      foundMimeType = true
    }
  })
  // Try to get details by file extension
  if (!foundMimeType) {
    if (EXTENSIONS[extension]) {
      displayDetails = EXTENSIONS[extension]
      // set preview path for images
      if (displayDetails.icon === 'fa-file-image-o')
        displayDetails.preview = url
    }
  }

  return displayDetails
}

export const getLabel = (asset) => {
  if (asset.extension === 'catalog') {
    return 'Media Finder'
  } else if (asset.asset_type !== 'asset') {
    return upperFirst(asset.asset_type)
  } else if (asset.extension) {
    if (EXTENSIONS[asset.extension]) {
      return EXTENSIONS[asset.extension].label
    }
  }
  return 'File'
}

export const getFileExtension = (filename) => {
  const a = filename.split('.')
  if (a.length === 1 || (a[0] === '' && a.length === 2)) {
    return ''
  }
  return a.pop().toLowerCase()
}

/**
 * Creates S3 object (using PutObject) specified by `params` and
 * returns signed URL for that object.  Note that this is often
 * used to create an empty object which is subsequently populated
 * by the `fetchSaga`...for reasons I don't fully grok.
 *
 * @param {PutObjectCommandInput} params
 * @param {Object} config
 * @param {string} config.region
 * @returns string
 */
export async function getS3Url(params, { region }) {
  const credentials = await Auth.currentCredentials()
  const client = new S3Client({
    credentialDefaultProvider: () => Auth.essentialCredentials(credentials),
    region,
  })
  const command = new PutObjectCommand(params)
  const request = await createRequest(client, command)
  const signer = new S3RequestPresigner({ ...client.config })
  const signedUrl = await signer.presign(request)

  return formatUrl(signedUrl)
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce(func, wait, immediate) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

// Set limits on how often a function can be called
export const throttle = (func, limit) => {
  let throttled
  return function () {
    const args = arguments
    const context = this
    if (!throttled) {
      func.apply(context, args)
      throttled = true
      setTimeout(() => (throttled = false), limit)
    }
  }
}

/*
 *   The right click action handler for the folder tree in the sidebar
 */
export function handleRightClick(e, data) {
  if (data.action === folderConstants.DELETE_FOLDER_ACTION) {
    // If the folder being clicked is not the active folder, set active folder
    if (data.name !== data.folders.activeFolder) {
      data.folderActions.activeFolder(data.name)
    }
    // Do not allow delete of folder if it contains referenced assets or referenced folders
    if (
      !folderContainsReferencedAssets(
        data.name,
        data.relationships.assets,
        data.folders.byId,
        data.endpoints.endpoints,
      ) &&
      !folderContainsReferencedFolders(
        data.name,
        data.folders.byId,
        data.relationships.folders,
      )
    )
      return data.modalActions.openModal('DeleteModal', {
        type: 'folder',
        folderDragData: { over: null, above: null, below: null },
      })

    data.modalActions.setMessage({
      type: 'ERROR',
      msg:
        'Invalid Action: cannot delete folders that contain assets or folders that are ' +
        'referenced by Builder Assets',
    })
    data.modalActions.openModal('MessageModal')
  }
  if (data.action === folderConstants.NEW_FOLDER_ACTION) {
    // If the folder being clicked is not the active folder, set active folder
    if (data.name !== data.folders.activeFolder) {
      data.folderActions.activeFolder(data.name)
    }
    // Do not allow add folder in recycle bin
    if (data.name === data.folders.recycleBin || data.name === '_search') {
      data.modalActions.setMessage({
        type: 'ERROR',
        msg: 'Invalid Target: cannot add folders to recycle bin or search folder',
      })
      data.modalActions.openModal('MessageModal')
      return
    }
    data.toggle()
    const ep = getEndpointOfNode(
      data.folders.byId[data.name],
      data.folders.byId,
      data.endpoints.endpoints,
    )

    data.modalActions.openModal('AddFolderModal', {
      treeRoot: ep.rootFolders,
      onToggle: data.toggle,
      onNodeClick: data.onNodeClick,
      endpointType: ep.type,
    })
  }
  if (data.action === folderConstants.EMPTY_RECYCLE_BIN_ACTION) {
    Modal.confirm({
      title: 'Empty Recycle Bin',
      content: 'This action cannot be undone.',
      onOk: () => {
        data.folderActions.emptyRecycleBin(
          data.folders.byId[data.folders.recycleBin],
          data.folders.recycleBin,
        )
      },
      onCancel: () => {
        Promise.resolve()
      },
    })
  }
  if (data.action === folderConstants.PUBLISH_FOLDER_ACTION) {
    Modal.confirm({
      title: 'Publish Folder',
      content:
        'Publish the Folder, including all subfolders and assets contained within?',
      okText: 'Proceed',
      cancelText: 'Cancel',
      onOk: () => {
        data.modalActions.openModal('PublishFolderModal', {
          folder: data.name,
        })
      },
      onCancel: () => {
        Promise.resolve()
      },
    })
  }
  if (data.action === folderConstants.UNPUBLISH_FOLDER_ACTION) {
    if (data.name !== data.folders.activeFolder) {
      data.folderActions.activeFolder(data.name)
    }
    Modal.confirm({
      title: 'Unpublish Folder',
      content:
        'Would you like to unpublish this folder and all assets and subfolders within it?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: () => {
        data.endpointActions.unpublishFolder(data.name)
      },
      onCancel: () => {
        Promise.resolve()
      },
    })
  }
  if (data.action === folderConstants.RENAME_FOLDER_ACTION) {
    data.appActions.setRenamingFolder(data.name)
  }

  if (data.action === folderConstants.SET_PERMISSIONS_ACTION) {
    // If the folder being clicked is not the active folder, set active folder
    if (data.name !== data.folders.activeFolder) {
      data.folderActions.activeFolder(data.name)
    }
    data.modalActions.openModal('FolderPermissionsModal', { folder: data.name })
  }
}

/*
 *  A function to enable downloading of assets.  It adds a link to the page, clicks it, then
 *  removes it
 *  Note: special handling for comparison data (we don't want to download the asset json, we
 *  want the comparison data sheet)
 *  Note Note: there's also related code in the downloads microservice
 */
export const downloadAsset = (
  item,
  useTranscodedVideo = false,
  recordEvent = true,
) => {
  const link = document.createElement('a')
  const params = []
  let downloadName = item.filename

  /*
   * rehostAssetUrl first, while the url still says /active/.
   *
   * /download/<key> is not a real object key -- it only resolves because
   * bam-api-download's downloadRequest edge function rewrites it and attaches
   * Content-Disposition, and that function only exists on the DAM
   * distribution. Stored urls point at whatever host was configured when the
   * asset was saved (app.bam.zone, a tenant domain), which is now served by
   * the mp web distribution, where the path is asked of S3 verbatim and comes
   * back NoSuchKey. Requesting it from our own origin restores the rewrite.
   */
  const assetUrl = rehostAssetUrl(item.url)

  link.href = assetUrl.replace(/active/, 'download')

  if (useTranscodedVideo) {
    // The transcode is always mp4, but the original filename usually already
    // ends in .mp4 -- appending unconditionally produced "clip.mp4.mp4"
    if (!downloadName.toLowerCase().endsWith(`.${TRANSCODING_EXTENSION}`)) {
      downloadName = `${downloadName}.${TRANSCODING_EXTENSION}`
    }
    params.push({ key: 'transcoded', value: true })

    link.href = assetUrl
      .substring(0, assetUrl.indexOf('active'))
      .concat(item.videoSrc.sources[0].replace(/videos/, 'download'))
  } else if (item.asset_type === COMPARISON__BUILDER_TYPE) {
    downloadName = downloadName.replace(/\.comparison$/, '.tsv')
    params.push({ key: 'comparison', value: true })

    link.href = assetUrl
      .substring(0, assetUrl.indexOf('active'))
      .concat(item.comparison.datafileKey.replace(/comparisons/, 'download'))
  } else if (item.asset_type === SOLUTION__BUILDER_TYPE) {
    // Built from the rehosted url rather than item.url's own domain, for the
    // same reason as the branches above
    const domain = assetUrl && assetUrl.split('/')[2]
    const path =
      item &&
      item.solution &&
      item.solution.pdf &&
      item.solution.pdf.s3Key.replace('active', 'download')
    const downloadUrl = `https://${domain}/${path}`
    link.href = downloadUrl

    downloadName = item.filename.replace('.solution', '.pdf')
  } else if (downloadName && item.extension) {
    /*
     * filename is user-editable, and renaming in the sidebar/edit modal lets
     * the extension be dropped. The stored extension is authoritative, so
     * restore it -- otherwise Content-Disposition names an extensionless file
     * the OS has no idea how to open.
     */
    if (
      !downloadName.toLowerCase().endsWith(`.${item.extension.toLowerCase()}`)
    ) {
      downloadName = `${downloadName}.${item.extension}`
    }
  }

  params.push({ key: 'name', value: downloadName })
  link.href += `?${params
    .reduce((acc, curr) => acc.concat(`${curr.key}=${curr.value}`), [])
    .join('&')}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  if (recordEvent) {
    recordAnalyticsEvent(analyticsConstants.DOWNLOAD_ASSET, {
      asset: item,
    }).catch(console.error)
  }
}

/*
 *  Given a folder, gets it's root folder.
 *  The root folder may have special handling, and can be useful to look up the endpoint
 */
export function getRootOfNode(folder, allFolders) {
  let parent = folder.parentId

  // BAM-2502 may not exist due to being filtered out from privileges
  if (parent && allFolders[parent]) {
    return getRootOfNode(allFolders[parent], allFolders)
  } else {
    return folder.id
  }
}

/*
 *  Given a folder ID, return the endpoint that it belongs to
 */
export function getEndpointOfNode(folder, allFolders, endpoints) {
  const root = getRootOfNode(folder, allFolders)
  let ep = {}
  endpoints.forEach((endpoint) => {
    if (endpoint.rootFolders.indexOf(root) > -1) ep = endpoint
  })
  return ep
}

/*
 * Given an endpoint ID, return the endpoint object
 */
export function getEndpointById(id, endpoints) {
  let ep = null
  endpoints.forEach((endpoint) => {
    if (endpoint.id === id) {
      ep = endpoint
    }
  })
  return ep
}

export const getEndpointStatus = (
  folder,
  asset,
  folderAssetStatus,
  options = {},
) => {
  const {
    endpointType = 'mst',
    specialtyIcon = false,
    tooltipPlacement = 'top',
    tooltipTitle = null,
  } = options
  const status = folderAssetStatus[folder][asset]?.status
  let icon = null
  switch (status) {
    case 'PENDING_APPROVAL':
      icon = (
        <Tooltip
          title={tooltipTitle ? tooltipTitle : 'Pending Approval'}
          placement={tooltipPlacement}
        >
          <i className="fa fa-check-circle-o assettable__status-yellow" />
        </Tooltip>
      )
      break
    case 'PENDING_REMOVAL':
      icon = (
        <Tooltip
          title={tooltipTitle ? tooltipTitle : 'Pending Removal'}
          placement={tooltipPlacement}
        >
          <i className="fa fa-exclamation-circle assettable__status-red" />
        </Tooltip>
      )
      break
    case 'APPROVED':
      icon = (
        <Tooltip
          title={tooltipTitle ? tooltipTitle : 'Published'}
          placement={tooltipPlacement}
        >
          <i
            className={clsx('fa assettable__status-green', {
              'fa-check-circle': !specialtyIcon,
              'fa-mobile': specialtyIcon && endpointType !== WEB_ENDPOINT_TYPE,
              'fa-globe': specialtyIcon && endpointType === WEB_ENDPOINT_TYPE,
            })}
          />
        </Tooltip>
      )
      break
    default:
      icon = null
  }
  return icon
}
export const getEndpointStatusText = (folder, asset, folderAssetStatus) => {
  const status = folderAssetStatus[folder][asset].status
  let text
  switch (status) {
    case 'PENDING_APPROVAL':
    case 'PENDING_REMOVAL':
      text = 'Waiting For Approval'
      break
    case 'APPROVED':
      text = 'Published'
      break
    case 'UNPUBLISHED':
      text = 'Unpublished'
      break
    default:
      text = 'Original Asset'
  }
  return text
}
/*
 *   Return the number of assets in an endpoint with a PENDING_APPROVAL status
 */
export function getEndpointPendingCount(
  children,
  folderAssetStatus,
  allFolders,
  type = 'PENDING_APPROVAL',
) {
  let count = 0
  children.forEach((folder) => {
    count =
      count + getFolderPendingCount(folder, folderAssetStatus, type, allFolders)
    if (
      allFolders[folder] &&
      (allFolders[folder].childFolders || []).length > 0
    ) {
      count =
        count +
        getEndpointPendingCount(
          allFolders[folder].childFolders,
          folderAssetStatus,
          allFolders,
          type,
        )
    }
  })
  return count
}

/*
 *   Return the number of assets in a folder with a PENDING_APPROVAL status
 */
export function getFolderPendingCount(
  folder,
  folderAssetStatus,
  type = 'PENDING_APPROVAL',
  allFolders,
) {
  let count = 0
  const statuses = (folderAssetStatus && folderAssetStatus[folder]) || {}
  if (allFolders[folder]) {
    ;(allFolders[folder].assets || []).forEach((asset) => {
      const assetState = statuses[asset]
      if (assetState && assetState.status === type) {
        count = count + 1
      }
    })
  }
  return count
}

/*
 *   Return the number of assets in a folder with a PENDING_APPROVAL status
 */
export function getRolledUpFolderPendingCount(
  folder,
  allFolders,
  folderAssetStatus,
  type = 'PENDING_APPROVAL',
) {
  let count = getFolderPendingCount(folder, folderAssetStatus, type, allFolders)
  if (!allFolders[folder]) return count
  ;(allFolders[folder].childFolders || []).forEach((child) => {
    count =
      count +
      getRolledUpFolderPendingCount(child, allFolders, folderAssetStatus, type)
  })
  return count
}

/*
 *  Helper to get the correct count for badges in the sidebar
 */
export const getEndpointBadgeCount = (
  badgeType,
  rootFolders,
  folderAssetStatus,
  allFolders,
) => {
  switch (badgeType) {
    case 'UGC':
      if (!allFolders[folderConstants.UGC_FOLDER]) return 0

      return allFolders[folderConstants.UGC_FOLDER].assets.length || 0
    case 'PENDING_APPROVAL':
    case 'PENDING_REMOVAL':
      return getEndpointPendingCount(
        rootFolders,
        folderAssetStatus,
        allFolders,
        badgeType,
      )
    default:
      return 0
  }
}

/*
 *   SYS. tags are applied by the system when an asset meets certain criteria;
 *   For now, the criteria is missing metadata (tags, title, description)
 *   When an asset is created or edited this function adds or removes the SYS. tags
 *   based on the asset's metadata conditions.
 */
export function validateSysTags(asset) {
  const tagCount = asset.tags.filter((tag) => !tag.startsWith('SYS.')).length
  const hasDescription = asset.description && asset.description.length > 0
  const hasTitle = asset.title && asset.title.length > 0

  if (tagCount === 0) {
    if (asset.tags.indexOf('SYS.missingtags') < 0) {
      asset.tags.push('SYS.missingtags')
    }
  } else {
    if (asset.tags.indexOf('SYS.missingtags') > -1) {
      asset.tags.splice(asset.tags.indexOf('SYS.missingtags'), 1)
    }
  }

  if (!hasDescription) {
    if (asset.tags.indexOf('SYS.missingdescription') < 0) {
      asset.tags.push('SYS.missingdescription')
    }
  } else {
    if (asset.tags.indexOf('SYS.missingdescription') > -1) {
      asset.tags.splice(asset.tags.indexOf('SYS.missingdescription'), 1)
    }
  }

  if (!hasTitle) {
    if (asset.tags.indexOf('SYS.missingtitle') < 0) {
      asset.tags.push('SYS.missingtitle')
    }
  } else {
    if (asset.tags.indexOf('SYS.missingtitle') > -1) {
      asset.tags.splice(asset.tags.indexOf('SYS.missingtitle'), 1)
    }
  }

  return asset
}

/*
 * Checks if user has the ability to create content (assets, news, comparison, navigator, product)
 * Looks for SYS level groups first; then checks folder tree for user-specific groups and roles
 */
export const isContentAdmin = (user, groups) => {
  const userGroups = getGroupsByUser(user, groups)
  return userGroups.some((grp) => grp === 'SYS.contentadmins')
}

/*
 *  Given a privilege name and the set of all privileges, return the role IDs that have this
 *  privilege
 */
export function getRolesWithPrivilege(requestedPrivilege, privileges) {
  return Object.keys(privileges[requestedPrivilege] || []).filter(
    (key) => privileges[requestedPrivilege][key] === true,
  )
}

/*
 *  Return an array of groups containing this user in members
 */
export const getGroupsByUser = (user, groups) => {
  return Object.keys(groups).filter(
    (grp) => groups[grp].members.indexOf(user) > -1,
  )
}

export const validatePrivilege = (
  requestedPrivilege,
  privileges = [],
  user,
  groups,
  folderToCheck,
  allFolders,
) => {
  if (folderToCheck === '_search' && requestedPrivilege === 'viewfolder')
    return true

  // if user is a content admin or admin, allow all actions
  const userGroups = getGroupsByUser(user, groups)
  if (
    userGroups.includes('SYS.admins') ||
    userGroups.includes('SYS.contentadmins')
  ) {
    return true
  }

  // Check whether active folder exists in this user's state
  // Convert single folder to array
  if (!Array.isArray(folderToCheck)) {
    if (!(folderToCheck && allFolders[folderToCheck])) {
      return false
    }
    folderToCheck = [folderToCheck]
  }

  let privilegeFound = false

  folderToCheck.forEach((folder) => {
    if (!privilegeFound) {
      // Consumed shared-endpoint folders arrive without contentRoles
      const contentRoles = (allFolders[folder] || {}).contentRoles || {}
      //console.log(`this folder's contentRoles:`, contentRoles)
      const validRoles = getRolesWithPrivilege(requestedPrivilege, privileges)
      //console.log(`roles with ${requestedPrivilege}`, validRoles)
      if (
        contentRoles[user] &&
        validRoles.some((r) => contentRoles[user].roleIds.indexOf(r) > -1)
      ) {
        privilegeFound = true
      } else {
        //console.log('userGroups', userGroups)

        // Filter the content roles by groups that contain the valid roles (we already checked user)
        // Check if user is a member of any of those groups
        if (
          Object.keys(contentRoles)
            .filter((cr) => contentRoles[cr].entityType === 'group')
            .filter((cr) => userGroups.indexOf(cr) > -1)
            .filter((cr) =>
              contentRoles[cr].roleIds.some((r) => validRoles.indexOf(r) > -1),
            ).length > 0
        ) {
          privilegeFound = true
        }
      }
    }
  })

  return privilegeFound
}

export const isAdmin = (user, groups) => {
  return groups['SYS.admins'] && groups['SYS.admins'].members.indexOf(user) > -1
}

export const folderHasContentRoleGroup = (folder, allFolders, group) => {
  // Consumed shared-endpoint folders arrive without contentRoles
  const contentRoles = (allFolders[folder] || {}).contentRoles || {}
  return !!(contentRoles[group] && contentRoles[group].roleIds.length > 0)
}

/**
 * @param {Object} groups all groups used to generate privileges
 * @param {"mst"|"web"} endpointType type of the endpoint containing the folder
 */
export const generateDefaultContentRoles = (groups, endpointType) => {
  const contentRoles = {
    'SYS.approvers': {
      entityType: 'group',
      entityId: groups['SYS.approvers'].id,
      roleIds: ['approver'],
    },
    'SYS.contentadmins': {
      entityType: 'group',
      entityId: groups['SYS.contentadmins'].id,
      roleIds: ['contentadmin'],
    },
    'SYS.creators': {
      entityType: 'group',
      entityId: groups['SYS.creators'].id,
      roleIds: ['creator'],
    },
    'SYS.editors': {
      entityType: 'group',
      entityId: groups['SYS.editors'].id,
      roleIds: ['editor'],
    },
    'SYS.publishers': {
      entityType: 'group',
      entityId: groups['SYS.publishers'].id,
      roleIds: ['publisher'],
    },
    'SYS.traversers': {
      entityType: 'group',
      entityId: groups['SYS.traversers'].id,
      roleIds: ['traverser'],
    },
    'SYS.viewers': {
      entityType: 'group',
      entityId: groups['SYS.viewers'].id,
      roleIds: ['viewer'],
    },
  }

  switch (endpointType) {
    case endpointConstants.MST_ENDPOINT_TYPE:
      contentRoles['SYS.mobileusers'] = {
        entityType: 'group',
        entityId: groups['SYS.mobileusers'].id,
        roleIds: ['mobileuser'],
      }

      break

    default:
      break
  }

  return contentRoles
}

export const getBulkAction = (config) => {
  let func = null
  switch (config.action) {
    case 'selectall':
      func = () => {
        if (config.selectedFilter !== 'ALL') {
          if (
            config.activeFolder &&
            config.selectedAssets.length !==
              config.allFolders[config.activeFolder].assets.filter((asset) => {
                return (
                  config.publishedStatus[config.activeFolder] &&
                  config.publishedStatus[config.activeFolder][asset] &&
                  config.publishedStatus[config.activeFolder][asset].status ===
                    config.selectedFilter
                )
              }).length
          ) {
            config.appActions.selectAssets(
              config.allFolders[config.activeFolder].assets.filter((asset) => {
                return (
                  config.publishedStatus[config.activeFolder] &&
                  config.publishedStatus[config.activeFolder][asset] &&
                  config.publishedStatus[config.activeFolder][asset].status ===
                    config.selectedFilter
                )
              }),
            )
          } else {
            config.appActions.clearSelectedAssets()
          }
        } else {
          if (
            config.activeFolder &&
            config.selectedAssets.length !==
              config.allFolders[config.activeFolder].assets.length
          ) {
            config.appActions.selectAssets(
              config.allFolders[config.activeFolder].assets,
            )
          } else {
            config.appActions.clearSelectedAssets()
          }
        }
      }
      break
    case 'edit':
      func = () => {
        if (
          config.selectedAssets.reduce((acc, asset) => {
            acc = acc.concat(config.allAssets[asset].publishedTo)
            return acc
          }, []).length > 0
        )
          return Modal.confirm({
            width: 550,
            title: 'Published Asset - Continue?',
            onOk: () => {
              if (config.selectedAssets.length > 1)
                config.modalActions.openModal('BulkEditModal', {
                  assets: config.selectedAssets.map(
                    (asset) => config.allAssets[asset],
                  ),
                  new: false,
                })
              else
                config.modalActions.openModal('EditAssetModal', {
                  asset: config.selectedAssets.map(
                    (asset) => config.allAssets[asset],
                  )[0],
                  new: false,
                })

              config.appActions.clearSelectedAssets()
              Promise.resolve('')
            },
            okText: 'Continue',
            content: (
              <div>
                <div>Edit will automatically display on published assets.</div>
                <AssetSelector
                  assets={config.selectedAssets}
                  allAssets={config.allAssets}
                  allFolders={config.allFolders}
                  updateSelected={(assets) => (config.selectedAssets = assets)}
                />
              </div>
            ),
          })
        else {
          if (config.selectedAssets.length > 1)
            return config.modalActions.openModal('BulkEditModal', {
              assets: config.selectedAssets.map(
                (asset) => config.allAssets[asset],
              ),
              new: false,
            })
          else
            return config.modalActions.openModal('EditAssetModal', {
              asset: config.selectedAssets.map(
                (asset) => config.allAssets[asset],
              )[0],
              new: false,
            })
        }
      }
      break
    case 'delete':
      func = () => {
        if (
          config.activeFolder === folderConstants.UGC_FOLDER ||
          (config.activeFolder &&
            config.allFolders[config.activeFolder].parentId ===
              folderConstants.UGC_FOLDER)
        ) {
          return Promise.all([
            config.assetActions.reviewUgcAssets(
              config.selectedAssets,
              UGC_REJECTED_STATUS,
              config.recycleBin,
              config.history,
            ),
            config.appActions.clearSelectedAssets(),
          ])
        }

        if (
          config.selectedAssets.reduce((acc, asset) => {
            acc = acc.concat(config.allAssets[asset].publishedTo)
            return acc
          }, []).length > 0
        ) {
          return Modal.confirm({
            width: 550,
            title: 'Published Asset - Continue?',
            onOk: () => {
              config.assetActions.dropAsset(
                config.recycleBin,
                config.selectedAssets,
                config.activeFolder,
              )
              config.appActions.clearSelectedAssets()
              return Promise.resolve('')
            },
            okText: 'Continue',
            content: (
              <div>
                <div>Edit will automatically display on published assets.</div>
                <AssetSelector
                  assets={config.selectedAssets}
                  allAssets={config.allAssets}
                  allFolders={config.allFolders}
                  updateSelected={(assets) => (config.selectedAssets = assets)}
                />
              </div>
            ),
          })
        } else {
          return Promise.all([
            config.assetActions.dropAsset(
              config.recycleBin,
              config.selectedAssets,
              config.activeFolder,
            ),
            config.appActions.clearSelectedAssets(),
          ])
        }
      }

      break
    case 'download':
      func = () => config.assetActions.bulkDownload(config.selectedAssets)
      break
    case 'share':
      func = () =>
        config.modalActions.openModal('ShareAssetModal', {
          assets: config.selectedAssets,
          user: config.user,
          title:
            config.selectedAssets.length > 1 ? 'Share Assets' : 'Share Asset',
        })
      break
    case 'move':
      func = () =>
        config.modalActions.openModal('ReviewUGCModal', {
          assets: config.selectedAssets,
          treeRoot: getEndpointById(config.endpoint, config.endpoints)
            .rootFolders,
        })
      break
    case 'publish':
      func = () =>
        config.modalActions.openModal('PublishAssetModal', {
          assets: config.selectedAssets,
          parent: config.activeFolder,
        })
      break
    case 'review':
      func = () =>
        config.saveFormRef(
          Modal.info({
            title: (
              <span>
                Review Assets for Publishing &nbsp;
                <Tooltip
                  title={
                    'Publishing an asset requires approval. A published asset will be visible ' +
                    'to consumers of this endpoint'
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            ),
            okText: 'Cancel',
            onOk: () => Promise.resolve({}),
            okType: 'default',
            maskClosable: false,
            content: (
              <ReviewAssetForm
                assets={config.selectedAssets}
                reviewFunc={config.endpointActions.reviewAsset}
                statusType={'PENDING_APPROVAL'}
                activeFolder={config.activeFolder}
                allAssets={config.allFolders}
                closeForm={config.closeReviewForm}
              />
            ),
          }),
        )

      break
    case 'unpublish':
      func = () => {
        const approveFunc = config.unpublishAndApprove
          ? () =>
              config.endpointActions.unpublishAndApprove(
                config.selectedAssets,
                config.activeFolder,
              )
          : null
        config.endpointActions.unpublish(
          config.selectedAssets,
          config.activeFolder,
          () =>
            config.endpointActions.unpublishAsset(
              config.selectedAssets,
              config.activeFolder,
            ),
          approveFunc,
        )
      }
      break

    case 'unpublish_review':
      func = () =>
        config.saveFormRef(
          Modal.info({
            title: (
              <span>
                Review Assets for Removal &nbsp;
                <Tooltip
                  title={
                    'Removing a published asset requires approval.  Once an asset is removed, it' +
                    ' is no longer visible to consumers of this endpoint'
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            ),
            okText: 'Cancel',
            onOk: () => Promise.resolve({}),
            okType: 'default',
            maskClosable: false,
            content: (
              <ReviewAssetForm
                assets={config.selectedAssets}
                reviewFunc={config.endpointActions.reviewUnpublishAsset}
                statusType={'PENDING_REMOVAL'}
                activeFolder={config.activeFolder}
                allAssets={config.allAssets}
                closeForm={config.closeReviewForm}
              />
            ),
          }),
        )
      break

    default:
      func = null
  }
  return func
}

export const containsPublishedAsset = (
  folder,
  allFolders,
  allAssets,
  relationships,
) => {
  if (!allFolders[folder] || !allFolders[folder].assets) {
    return false
  }

  let hasPublishedAsset = false

  allFolders[folder].assets.forEach((asset) => {
    if (assetIsPublished(asset, relationships, allAssets)) {
      hasPublishedAsset = true
    }
  })
  if (hasPublishedAsset) {
    return true
  }
  allFolders[folder].childFolders.forEach((folder) => {
    hasPublishedAsset = containsPublishedAsset(
      folder,
      allFolders,
      allAssets,
      relationships,
    )
  })
  return hasPublishedAsset
}

export const getWidestPanel = () => {
  let widest = DEFAULT_SIDEBAR_WIDTH
  const list = Array.from(
    document.getElementsByClassName('ant-collapse-content-box'),
  )
  if (list) {
    list.forEach((div) => {
      if (div.offsetWidth > widest) {
        widest = div.offsetWidth
      }
    })
  }
  return widest
}

export const parseBuilderAssetData = (
  builderType,
  builderData,
  allAssets,
  user,
  builderSettings,
) => {
  let assetMetaData = {}
  let assetData = {}

  switch (builderType) {
    case 'news':
      assetMetaData = {
        id: builderData.id,
        asset_type: NEWS__BUILDER_TYPE,
        owner: user.id,
        title: builderData.title,
        description: builderData.description,
        tags: builderData.tags,
        icon: 'fa-newspaper-o',
        color: '#22075e',
        preview: null,
      }
      assetData = {
        copyright: builderData.copyright,
        image: builderData.leadImage,
        content:
          builderData.contentType === 'text' ? builderData.textContent : null,
        pdfContent:
          builderData.contentType === 'pdf' ? builderData.pdfContent : null,
      }
      return {
        assetMetaData,
        assetData,
        ContentType: 'application/json',
        assetDataSerialized: JSON.stringify(assetData),
      }
    case 'product':
      assetMetaData = {
        id: builderData.id,
        asset_type: PRODUCT__BUILDER_TYPE,
        owner: user.id,
        title: builderData.title,
        description: builderData.description,
        tags: builderData.tags,
        icon: 'fa-cubes',
        color: '#22075e',
        preview: null,
      }
      assetData = {
        image: builderData.leadImage,
        slides: builderData.slides,
      }
      return {
        assetMetaData,
        assetData,
        ContentType: 'application/json',
        assetDataSerialized: JSON.stringify(assetData),
      }
    case 'feature':
      assetMetaData = {
        id: builderData.id,
        asset_type: FEATURE__BUILDER_TYPE,
        owner: user.id,
        title: builderData.title,
        description: builderData.description,
        tags: builderData.tags,
        icon: 'fa-bullseye',
        color: '#22075e',
        preview: null,
      }
      assetData = {
        image: builderData.leadImage,
        relatedAssets: builderData.relatedAssets,
      }
      return {
        assetMetaData,
        assetData,
        ContentType: 'application/json',
        assetDataSerialized: JSON.stringify(assetData),
      }
    case 'navigator':
      assetMetaData = {
        id: builderData.id,
        asset_type: NAVIGATOR__BUILDER_TYPE,
        owner: user.id,
        title: builderData.title,
        description: builderData.description,
        tags: builderData.tags,
        icon: 'fa-sitemap',
        color: '#22075e',
        preview: null,
      }
      assetData = {
        image: builderData.leadImage,
        slides: builderData.slides,
      }
      return {
        assetMetaData,
        assetData,
        ContentType: 'application/json',
        assetDataSerialized: JSON.stringify(assetData),
      }
    case 'comparison':
      assetMetaData = {
        id: builderData.id,
        asset_type: COMPARISON__BUILDER_TYPE,
        owner: user.id,
        title: builderData.title,
        description: builderData.description,
        tags: builderData.tags,
        icon: 'fa-columns',
        color: '#22075e',
        preview: null,
      }
      assetData = {
        image: builderData.leadImage,
        datafileKey: builderData.datafileKey,
        comparisonObject: builderData.comparisonObject,
        prevDatafileKey: builderData.prevDatafileKey || null,
        primaryClassification: builderData.primaryClassification,
        useDefaultDisclaimer: builderSettings.comparisons.allowCustomDisclaimer
          ? builderData.useDefaultDisclaimer
          : true,
        disclaimerText:
          builderSettings.comparisons.allowCustomDisclaimer ||
          builderData.useDefaultDisclaimer
            ? builderData.disclaimerText
            : builderSettings.globalDisclaimer,
      }
      // note that comparisons are handled uniquely in `parseBuilderAssetData`
      // and the eventual content type is TSV; at some point, this code can
      // be refactored to take advantage of that, but leaving this alone for now;
      // see comments in `assetSerializer` in src/components/builders/builderConfigs.ts
      // for more information
      return {
        assetMetaData,
        assetData,
        ContentType: 'application/json',
        assetDataSerialized: JSON.stringify(assetData),
      }
    default:
      const builderConfig = builderConfigs[builderType]
      if (!builderConfig) {
        console.warn(`unrecognized builder type: ${builderType}`)
        return null
      }
      return builderConfig.assetSerializer({
        builderType,
        builderData,
        allAssets,
        user,
        builderSettings,
      })
  }
}

/*
 *  Returns true if arr1 has same items as arr2 (shallow compare)
 */
export const compareFlatArrays = (arr1, arr2) => {
  if (arr1 === arr2) return true
  if (arr1 == null || arr2 == null) return false
  if (arr1.length !== arr2.length) return false
  const [arrayOne, arrayTwo] = [arr1, arr2].map((arr) => arr.slice().sort())
  for (let i = 0; i < arrayOne.length; i++) {
    if (arrayOne[i] !== arrayTwo[i]) return false
  }

  return true
}

/*
 *  Function returns an object with two arrays: added and removed
 *  These arrays contain object Ids whose referencedBy property was impacted
 *  by a builder edit action.
 *  Each builder asset has different references...
 */
export const getChangedReferences = (
  builderType,
  newAsset,
  oldAsset,
  allAssets,
) => {
  let result = {
    assetRefUpdates: {
      added: [],
      removed: [],
    },
    folderRefUpdates: {
      added: [],
      removed: [],
    },
  }
  const newRefs = getBuilderRefs(builderType, newAsset, allAssets)
  const oldRefs = getBuilderRefs(builderType, oldAsset, allAssets)

  result.assetRefUpdates.added = newRefs.relatedAssets.filter(
    (ref) => oldRefs.relatedAssets.indexOf(ref) < 0,
  )
  result.assetRefUpdates.removed = oldRefs.relatedAssets.filter(
    (ref) => newRefs.relatedAssets.indexOf(ref) < 0,
  )
  result.folderRefUpdates.added = newRefs.relatedFolders.filter(
    (ref) => oldRefs.relatedFolders.indexOf(ref) < 0,
  )
  result.folderRefUpdates.removed = oldRefs.relatedFolders.filter(
    (ref) => newRefs.relatedFolders.indexOf(ref) < 0,
  )
  return result
}

/*
 * Returns an array of asset IDs referenced by a given builder
 * The array only contains direct references - to get a full list, you'll need
 * to check for indirect references
 */
export const getBuilderRefs = (builderType, asset) => {
  let assetRefs = new Set()
  let folderRefs = new Set()
  switch (builderType) {
    case NEWS__BUILDER_TYPE:
      if (asset[builderType].image) assetRefs.add(asset[builderType].image)
      if (asset[builderType].pdfContent)
        assetRefs.add(asset[builderType].pdfContent)
      break

    case FEATURE__BUILDER_TYPE:
      if (asset[builderType].image) assetRefs.add(asset[builderType].image)

      asset[builderType].relatedAssets &&
        asset[builderType].relatedAssets.forEach((rel) => {
          rel.assetId && assetRefs.add(rel.assetId)
        })
      break

    case PRODUCT__BUILDER_TYPE:
      if (asset[builderType].image) assetRefs.add(asset[builderType].image)
      asset[builderType].slides.forEach((slide) => {
        if (slide.mainImage) assetRefs.add(slide.mainImage)

        slide.hotspots.forEach((hotspot) => {
          const target = resolveLinkedTarget(hotspot)
          if (!target) return
          if (target.type === 'folder') folderRefs.add(target.id)
          else assetRefs.add(target.id)
        })
      })
      break

    case NAVIGATOR__BUILDER_TYPE:
      if (asset[builderType].image) assetRefs.add(asset[builderType].image)
      asset[builderType].slides.forEach((slide) => {
        if (slide.mainImage) assetRefs.add(slide.mainImage)
        slide.navzones.forEach((zone) => {
          if (zone.targetType === 'asset') assetRefs.add(zone.targetId)
          if (zone.targetType === 'folder') folderRefs.add(zone.targetId)
        })
      })
      break

    case COMPARISON__BUILDER_TYPE:
      if (asset[builderType].image) assetRefs.add(asset[builderType].image)
      asset[builderType].comparisonObject &&
        Object.keys(asset[builderType].comparisonObject).forEach((item) => {
          if (
            asset[builderType].comparisonObject[item].meta &&
            (asset[builderType].comparisonObject[item].meta.itemImage ||
              asset[builderType].comparisonObject[item].meta.image)
          )
            assetRefs.add(
              asset[builderType].comparisonObject[item].meta.itemImage ||
                asset[builderType].comparisonObject[item].meta.image,
            )
        })
      break

    default:
      break
  }
  return {
    relatedAssets: Array.from(assetRefs),
    relatedFolders: Array.from(folderRefs),
  }
}
/*
 *  Checks for metadata changes and returns an object with new data values for merging
 *  Metadata includes: title, description, tags for all builders
 *  Metadata includes preview for news
 */
export const getChangedMetadata = (
  builderType,
  newAsset,
  oldAsset,
  allAssets,
  userId,
) => {
  let updates = {
    updated: Date.now(),
    updated_by: userId,
  }
  if (newAsset.version !== oldAsset.version) updates.version = newAsset.version

  if (newAsset.title !== oldAsset.title) updates.title = newAsset.title

  if (newAsset.description !== oldAsset.description)
    updates.description = newAsset.description

  if (!compareFlatArrays(newAsset.tags, oldAsset.tags))
    updates.tags = newAsset.tags

  if (newAsset[builderType].image !== oldAsset[builderType].image)
    updates.preview =
      allAssets[newAsset[builderType].image] &&
      allAssets[newAsset[builderType].image].url

  return updates
}

/*
 *  Checks for changes to the body of a builder asset
 *  Each builder has a different body structure.
 *  If body updates are detected, the builder should be re-saved to S3
 * If the specific builder type does not have custom logic to detect changes,
 * this function will do a shallow comparison of the top-level keys in the asset.
 */
export const getChangedBuilderData = (builderType, newAsset, oldAsset) => {
  let updates = {
    [builderType]: {},
  }

  switch (builderType) {
    case NEWS__BUILDER_TYPE: {
      if (newAsset[builderType].content !== oldAsset[builderType].content)
        updates[builderType].content = newAsset[builderType].content
      if (newAsset[builderType].image !== oldAsset[builderType].image)
        updates[builderType].image = newAsset[builderType].image
      if (newAsset[builderType].copyright !== oldAsset[builderType].copyright)
        updates[builderType].copyright = newAsset[builderType].copyright
      if (newAsset[builderType].pdfContent !== oldAsset[builderType].pdfContent)
        updates[builderType].pdfContent = newAsset[builderType].pdfContent
      break
    }
    case FEATURE__BUILDER_TYPE: {
      if (newAsset[builderType].image !== oldAsset[builderType].image)
        updates[builderType].image = newAsset[builderType].image
      if (
        JSON.stringify(newAsset[builderType].relatedAssets) !==
        JSON.stringify(oldAsset[builderType].relatedAssets)
      )
        updates[builderType].relatedAssets = newAsset[builderType].relatedAssets
      break
    }
    case PRODUCT__BUILDER_TYPE:
    case NAVIGATOR__BUILDER_TYPE: {
      if (newAsset[builderType].image !== oldAsset[builderType].image)
        updates[builderType].image = newAsset[builderType].image
      //Todo - deep equal compare slides
      updates[builderType].slides = newAsset[builderType].slides
      break
    }
    case COMPARISON__BUILDER_TYPE: {
      if (newAsset[builderType].image !== oldAsset[builderType].image)
        updates[builderType].image = newAsset[builderType].image

      if (
        newAsset[builderType].datafileKey !== oldAsset[builderType].datafileKey
      )
        updates[builderType].datafileKey = newAsset[builderType].datafileKey

      if (
        newAsset[builderType].prevDatafileKey !==
        oldAsset[builderType].prevDatafileKey
      )
        updates[builderType].prevDatafileKey =
          newAsset[builderType].prevDatafileKey

      if (
        newAsset[builderType].primaryClassification !==
        oldAsset[builderType].primaryClassification
      )
        updates[builderType].primaryClassification =
          newAsset[builderType].primaryClassification

      if (
        newAsset[builderType].useDefaultDisclaimer !==
        oldAsset[builderType].useDefaultDisclaimer
      )
        updates[builderType].useDefaultDisclaimer =
          newAsset[builderType].useDefaultDisclaimer

      if (
        newAsset[builderType].disclaimerText !==
        oldAsset[builderType].disclaimerText
      )
        updates[builderType].disclaimerText =
          newAsset[builderType].disclaimerText

      if (
        !isEqual(
          newAsset[builderType].comparisonObject,
          oldAsset[builderType].comparisonObject || {},
        )
      )
        updates[builderType].comparisonObject =
          newAsset[builderType].comparisonObject
      break
    }
    default: {
      // if no special behavior is specified, do a shallow equality comparison
      const keys = Array.from(
        new Set([
          ...Object.keys(oldAsset[builderType]),
          ...Object.keys(newAsset[builderType]),
        ]),
      )
      for (const key of keys) {
        if (!key) return

        if (newAsset[builderType][key] !== oldAsset[builderType][key])
          updates[builderType][key] = newAsset[builderType][key]
      }
      break
    }
  }

  return updates
}

/*
 *  Builds an array of event objects to save with the action
 *  These events show:
 *   who: the user that made the change
 *   what: the attributes that were changed
 *   where: the asset id that was changed
 *   when: The timestamp of the change
 *   how: the action the caused the change
 */
export const generateBuilderEvents = (
  builderId,
  builderType,
  refUpdates,
  metadataUpdates,
  bodyUpdates,
  userId,
  originalData,
) => {
  let events = []
  let timestamp = Date.now()
  const builderEvent = {
    event_type: 'UPDATE_ASSET',
    target_id: builderId,
    timestamp,
    user: userId,
    attributes: [],
  }
  Object.keys(metadataUpdates).forEach((key) => {
    timestamp = timestamp + 1
    builderEvent.attributes.push({
      name: key,
      new_value: metadataUpdates[key],
      old_value: originalData[key],
    })
  })

  Object.keys(bodyUpdates[builderType]).forEach((key) => {
    builderEvent.attributes.push({
      name: key,
      new_value: bodyUpdates[builderType][key],
      old_value: originalData[builderType][key],
    })
  })
  events.push(builderEvent)

  refUpdates.assetRefUpdates.added.forEach((ref) => {
    timestamp = timestamp + 1
    events.push({
      event_type: 'UPDATE_ASSET',
      target_id: ref,
      timestamp,
      user: userId,
      attributes: [
        {
          name: 'referencedBy',
          new_value: ref,
          old_value: '',
        },
      ],
    })
  })
  refUpdates.assetRefUpdates.removed.forEach((ref) => {
    timestamp = timestamp + 1
    events.push({
      event_type: 'UPDATE_ASSET',
      target_id: ref,
      timestamp,
      user: userId,
      attributes: [
        {
          name: 'referencedBy',
          new_value: ref,
          old_value: '',
        },
      ],
    })
  })
  refUpdates.folderRefUpdates.added.forEach((ref) => {
    timestamp = timestamp + 1
    events.push({
      event_type: 'UPDATE_ASSET',
      target_id: ref,
      timestamp,
      user: userId,
      attributes: [
        {
          name: 'referencedBy',
          new_value: ref,
          old_value: '',
        },
      ],
    })
  })
  refUpdates.folderRefUpdates.removed.forEach((ref) => {
    timestamp = timestamp + 1
    events.push({
      event_type: 'UPDATE_ASSET',
      target_id: ref,
      timestamp,
      user: userId,
      attributes: [
        {
          name: 'referencedBy',
          new_value: ref,
          old_value: '',
        },
      ],
    })
  })
  return events
}
/*
 *  Find first folder that contains a given asset
 */
/*
 *  Resolves the target of a linked reference (a product hotspot, a feature
 *  related asset, etc) to { type, id }. Handles both the legacy asset-only
 *  form ({ assetId }) and the navzone-style form ({ targetType, targetId }).
 *  Returns null when nothing is linked.
 */
export const resolveLinkedTarget = (ref) => {
  if (!ref) return null
  const targetType = ref.targetType || null
  const targetId = ref.targetId || null
  if (targetType === 'folder' && targetId)
    return { type: 'folder', id: targetId }
  const assetId = ref.assetId || targetId
  return assetId ? { type: 'asset', id: assetId } : null
}

/*
 *  Finds the first folder containing an asset within a given endpoint;
 *  defaults to the library ('LIB'), but the published content tree ('MST')
 *  is a valid endpoint too
 */
export const findFolderForAsset = (
  asset,
  allFolders,
  endpoints,
  endpointId = 'LIB',
) => {
  let firstFolder = null
  Object.keys(allFolders).forEach((folder) => {
    if (
      allFolders[folder].assets.indexOf(asset) > -1 &&
      getEndpointOfNode(allFolders[folder], allFolders, endpoints).id ===
        endpointId
    )
      firstFolder = folder
  })
  return firstFolder
}

export const findLibraryFolder = (asset, allFolders, endpoints) =>
  findFolderForAsset(asset, allFolders, endpoints)

export const scrollElement = (
  itemRef,
  element,
  start,
  change,
  duration = 20,
  direction = 'horizontal',
) => {
  const increment = 20
  const animateScroll = (elapsedTime) => {
    elapsedTime += increment
    if (direction === 'vertical')
      element.scrollTop = easeInOut(elapsedTime, start, change, duration)
    else element.scrollLeft = easeInOut(elapsedTime, start, change, duration)

    if (elapsedTime < duration) {
      setTimeout(function () {
        animateScroll(elapsedTime)
      }, increment)
    } else {
      itemRef.scrolling = false
    }
  }

  animateScroll(0)
}

function easeInOut(currentTime, start, change, duration) {
  currentTime /= duration / 2
  if (currentTime < 1) {
    return (change / 2) * currentTime * currentTime + start
  }
  currentTime -= 1
  return (-change / 2) * (currentTime * (currentTime - 2) - 1) + start
}

export const assetIsPublished = (
  assetId,
  relationships,
  allAssets,
  visited = [],
) => {
  let isPublished = false
  if (allAssets[assetId] && (allAssets[assetId].publishedTo || []).length > 0) {
    return true
  }
  visited.push(assetId)
  relationships.referencedBy[assetId] &&
    relationships.referencedBy[assetId].forEach((ref) => {
      if (!isPublished && visited.indexOf(ref) < 0)
        isPublished = assetIsPublished(ref, relationships, allAssets, visited)
    })
  return isPublished
}

export const assetIsReferenced = (assetId, relationships) => {
  let references = false
  if (
    !references &&
    relationships.referencedBy[assetId] &&
    relationships.referencedBy[assetId].length > 0
  )
    references = true
  return references
}

// This function will check if a folder has any assets that are referenced by other assets in
// a different folder
// It is used for validating whether a folder can be deleted by drag-n-drop
export const folderContainsReferencedAssets = (
  folder,
  relationships,
  allFolders,
  endpoints,
) => {
  let references = false
  let parent
  allFolders[folder].assets.forEach((asset) => {
    parent = findLibraryFolder(asset, allFolders, endpoints)
    if (!references && relationships.referencedBy[asset]) {
      relationships.referencedBy[asset].forEach((ref) => {
        if (
          !references &&
          findLibraryFolder(ref, allFolders, endpoints) !== parent
        ) {
          references = true
        }
      })
    }
  })
  allFolders[folder].childFolders.forEach((child) => {
    if (!references)
      references = folderContainsReferencedAssets(
        child,
        relationships,
        allFolders,
        endpoints,
      )
  })

  return references
}

// This function will check if a folder or any of its children is referenced by any builder
// assets (i.e. Navigators)
// It is used for validating whether a folder can be deleted
export const folderContainsReferencedFolders = (
  folder,
  allFolders,
  relationships,
) => {
  if (
    relationships.referencedBy[folder] &&
    relationships.referencedBy[folder].length > 0
  )
    return true

  let hasRefs = false
  allFolders[folder].childFolders.forEach((child) => {
    if (!hasRefs)
      hasRefs = folderContainsReferencedFolders(
        child,
        allFolders,
        relationships,
      )
  })

  return hasRefs
}

export const copySpanToClipboard = (spanId) => {
  var copyText = document.getElementById(spanId)
  var textArea = document.createElement('textarea')
  textArea.value = copyText.textContent
  textArea.setAttribute('readonly', '')
  textArea.style.position = 'absolute'
  textArea.style.left = '-9999px'
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand('Copy')
  textArea.remove()
}

/*
 *  This function determines whether a folder needs the traverser role for a given entity.
 *  Ignore folder allows you to ignore a given child folder - useful for validating the parent
 *  tree during drag and drop, when the folder move is not yet completed.
 *  Alternate content roles are used by bulk permissions updates where changes are not yet saved
 *  to redux state
 */
export const requiresTraverserRole = (
  folder,
  entity,
  allFolders,
  ignoreFolder = null,
  alternateContentRoles = null,
) => {
  let required = false
  if (entity === 'SYS.traversers') return true

  if (
    allFolders[folder].childFolders &&
    allFolders[folder].childFolders.length === 0
  )
    return false

  allFolders[folder].childFolders
    .filter((child) => child !== ignoreFolder)
    .forEach((child) => {
      // Check child for a non-traverser roles
      if (!required) {
        if (alternateContentRoles)
          required =
            alternateContentRoles[child][entity] &&
            alternateContentRoles[child][entity].roleIds.filter(
              (role) => role !== 'traverser',
            ).length > 0
        else
          required =
            allFolders[child].contentRoles[entity] &&
            allFolders[child].contentRoles[entity].roleIds.filter(
              (role) => role !== 'traverser',
            ).length > 0
      }
      // Check child subfolders for non-traverser role
      if (!required)
        required = requiresTraverserRole(
          child,
          entity,
          allFolders,
          ignoreFolder,
          alternateContentRoles,
        )
    })
  return required
}

export const assetIsUsedAsImage = (asset, allAssets, relationships) => {
  if (!asset) return false

  let isImage = false
  relationships.referencedBy[asset] &&
    relationships.referencedBy[asset].forEach((builder) => {
      if (
        !isImage &&
        allAssets[builder] &&
        allAssets[builder][allAssets[builder].asset_type].image === asset
      )
        isImage = true

      if (
        !isImage &&
        allAssets[builder] &&
        allAssets[builder].asset_type === COMPARISON__BUILDER_TYPE
      )
        isImage = true

      if (
        !isImage &&
        allAssets[builder] &&
        BUILDERS_WITH_SLIDES.indexOf(
          allAssets[builder][allAssets[builder].asset_type],
        ) > -1
      ) {
        allAssets[builder][allAssets[builder].asset_type].slides.forEach(
          (slide) => {
            if (slide.mainImage === asset) {
              isImage = true
            }
          },
        )
      }
    })

  return isImage
}

export const getUserById = (id, allUsers) => {
  return Object.values(allUsers).find((u) => u.id === id)
}

export const requiredFieldsArePopulated = (requiredFields, values, form) => {
  let populated = true
  requiredFields.forEach((field) => {
    if (typeof values[field] === 'string') values[field] = values[field].trim()

    if (!values[field]) {
      populated = false
      form.setFields({
        [field]: { errors: [new Error(`${field} is required`)] },
      })
    }
  })
  return populated
}

export const getOriginalAssetPreview = (asset) => {
  switch (asset.icon) {
    case 'fa-file-pdf-o':
      return `${asset.key}.${PDF_IMAGE_FILE_TYPE}`
    case 'fa-file-video-o':
      return `${asset.key}-thumbnail.0000000.jpg`
    default:
      return null
  }
}

// Asset paths the DAM's own CloudFront distribution serves out of the shared
// stage asset bucket, and which bam-api-thumbnailer is attached to.
const REHOSTABLE_PATHS = ['/active/', '/defaults/', '/resources/']

/*
 * Description
 * Points an asset url at the DAM's own origin.
 *
 * Asset urls are stored with whatever host was configured when the asset was
 * saved -- app.bam.zone, <tenant>.bam.zone, a custom sales domain. Those hosts
 * are now served by the mp web CloudFront distribution, which has no
 * thumbnailer: a `_thumb-<n>x` derivative that is not already in S3 comes back
 * 410 and the image renders blank. It cannot be generated on demand, and for a
 * source narrower than the requested width it can never exist at all, because
 * the thumbnailer refuses to enlarge (it 302s to the original instead).
 *
 * The DAM is itself served by the distribution that DOES have the thumbnailer,
 * so requesting the same key from our own origin restores both behaviors --
 * generate-on-demand, and redirect-to-original when it cannot enlarge. The
 * bucket is shared, so the path resolves to the same object either way.
 *
 * This is display only. The stored url is untouched, so nothing changes for
 * other clients (mp reads asset.preview straight out of state).
 *
 * @param    url    {string}    -   Asset url, absolute
 *
 * @return          {string}        The url on this origin, or unchanged if it
 *                                  is not a rehostable asset url
 */
export const rehostAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return url

  // In local development our origin is the dev server, which serves no assets
  // at all; leave urls pointing wherever they already point.
  const { hostname, origin } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') return url

  try {
    const parsed = new URL(url)

    if (parsed.origin === origin) return url
    // Only asset-bucket paths. Anything else (an external image, a link out)
    // means something different on our origin than it does on its own.
    //
    // The host is deliberately not checked: preview urls carry whatever domain
    // was configured when the asset was saved, including per-tenant sales
    // domains like salesiq.mclanahan.com, and there is no client-side list of
    // them. The assumption is that a url on one of these paths was written by
    // our own upload pipeline and so names our bucket. A genuinely third-party
    // image served from a /active/ path would be rewritten wrongly.
    if (!REHOSTABLE_PATHS.some((path) => parsed.pathname.startsWith(path))) {
      return url
    }

    return `${origin}${parsed.pathname}${parsed.search}`
  } catch (err) {
    // Relative or malformed; already resolves against our origin, or is not
    // ours to rewrite.
    return url
  }
}

/**
 * Converts a React element to its simple text content
 */
export function reactNodeToText(node) {
  if (typeof node === 'object') {
    if (React.isValidElement(node)) {
      return reactNodeToText(node.props.children)
    }

    return ''
  }

  return node
}

/*
 * Detect the device's IANA timezone (e.g. "America/Los_Angeles").
 * Returns '' when unavailable so callers can pass it through harmlessly;
 * the backend ignores empty/invalid values.
 */
export const detectTimezone = () => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch (e) {
    return ''
  }
}
